import * as THREE from 'three';

// The world around the original arena: a dark navy sky lit by the game's
// blue and purple, over a grid that runs out to the horizon. Matches the
// Classic menu backdrop.
const SKY_RADIUS = 1500;
const FLOOR_SIZE = 3000;

export class Backdrop {
  constructor(scene) {
    this.scene = scene;
    this.createSky();
    this.createFloor();
  }

  createSky() {
    this.sky = new THREE.Mesh(
      new THREE.SphereGeometry(SKY_RADIUS, 32, 16),
      new THREE.ShaderMaterial({
        side: THREE.BackSide,
        depthWrite: false,
        fog: false,
        uniforms: {
          baseColor: { value: new THREE.Color(0x080a14) },
          blueGlow: { value: new THREE.Color(0x1d3f8f) },
          purpleGlow: { value: new THREE.Color(0x46277a) }
        },
        vertexShader: /* glsl */`
          varying vec3 vDir;
          void main() {
            vDir = normalize(position);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: /* glsl */`
          uniform vec3 baseColor;
          uniform vec3 blueGlow;
          uniform vec3 purpleGlow;
          varying vec3 vDir;
          void main() {
            // Blue glow over the blue base (west), purple over the red base (east)
            float blue = pow(max(dot(vDir, normalize(vec3(-1.0, 0.25, -0.4))), 0.0), 3.0);
            float purple = pow(max(dot(vDir, normalize(vec3(1.0, 0.1, 0.5))), 0.0), 3.0);
            // A brighter band just above the horizon
            float horizon = exp(-abs(vDir.y) * 9.0) * 0.35;
            vec3 col = baseColor + blueGlow * (blue * 0.8 + horizon) + purpleGlow * purple * 0.8;
            gl_FragColor = vec4(col, 1.0);
            #include <colorspace_fragment>
          }
        `
      })
    );
    this.sky.frustumCulled = false;
    this.sky.renderOrder = -1;
    this.scene.add(this.sky);
  }

  // Faint grid lines that fade into the distance, below the arena floor
  createFloor() {
    const geometry = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE);
    geometry.rotateX(-Math.PI / 2);
    this.floor = new THREE.Mesh(geometry, new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: {
        lineColor: { value: new THREE.Color(0x26325a) },
        floorColor: { value: new THREE.Color(0x0c0f1e) },
        fadeDistance: { value: 900 }
      },
      vertexShader: /* glsl */`
        varying vec3 vWorld;
        void main() {
          vec4 world = modelMatrix * vec4(position, 1.0);
          vWorld = world.xyz;
          gl_Position = projectionMatrix * viewMatrix * world;
        }
      `,
      fragmentShader: /* glsl */`
        uniform vec3 lineColor;
        uniform vec3 floorColor;
        uniform float fadeDistance;
        varying vec3 vWorld;

        float gridLine(vec2 p, float spacing) {
          vec2 g = abs(fract(p / spacing - 0.5) - 0.5) / fwidth(p / spacing);
          return 1.0 - min(min(g.x, g.y), 1.0);
        }

        void main() {
          float fine = gridLine(vWorld.xz, 10.0) * 0.35;
          float major = gridLine(vWorld.xz, 50.0) * 0.8;
          float fade = 1.0 - smoothstep(150.0, fadeDistance, length(vWorld.xz));
          vec3 col = mix(floorColor, lineColor, max(fine, major) * fade);
          gl_FragColor = vec4(col, mix(0.0, 1.0, fade));
          #include <colorspace_fragment>
        }
      `
    }));
    this.floor.position.y = -0.05;
    this.floor.renderOrder = -0.5;
    this.scene.add(this.floor);
  }

  // Keep the sky centred on the camera's focus so it never clips
  update(focus) {
    if (focus) this.sky.position.set(focus.x, 0, focus.z);
  }
}
